import React, { useRef, useState, useCallback } from 'react'
import type { FileAttachment as FileAttachmentType } from '../types'

/** Accepted MIME types for file uploads */
const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp']
const ACCEPTED_PDF_TYPE = 'application/pdf'
const ACCEPTED_TYPES = [...ACCEPTED_IMAGE_TYPES, ACCEPTED_PDF_TYPE]

/** Maximum file size in bytes (5MB) */
const MAX_FILE_SIZE = 5 * 1024 * 1024

/** Maximum number of attachments per message */
const MAX_ATTACHMENTS = 5

interface FileAttachmentProps {
  /** Current list of attachments */
  attachments: FileAttachmentType[]
  /** Callback when attachments change */
  onAttachmentsChange: (attachments: FileAttachmentType[]) => void
  /** Whether the input is disabled */
  disabled?: boolean
}

/**
 * File attachment component with drag-and-drop and file picker support.
 *
 * Supports images (JPEG, PNG, GIF, WebP) and PDFs with preview capabilities.
 *
 * @param props - Component props
 * @returns JSX.Element The file attachment component
 */
const FileAttachment: React.FC<FileAttachmentProps> = ({
  attachments,
  onAttachmentsChange,
  disabled = false
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [isDragging, setIsDragging] = useState(false)
  const [error, setError] = useState<string | null>(null)

  /**
   * Converts a File to a FileAttachment with base64 encoding.
   */
  const fileToAttachment = useCallback(async (file: File): Promise<FileAttachmentType | null> => {
    // Validate file type
    if (!ACCEPTED_TYPES.includes(file.type)) {
      setError(`Invalid file type: ${file.type}. Accepted: images (JPEG, PNG, GIF, WebP) and PDFs.`)
      return null
    }

    // Validate file size
    if (file.size > MAX_FILE_SIZE) {
      setError(`File too large: ${(file.size / 1024 / 1024).toFixed(1)}MB. Maximum: 5MB.`)
      return null
    }

    return new Promise((resolve) => {
      const reader = new FileReader()
      reader.onload = () => {
        const base64 = reader.result as string
        // Remove the data URL prefix (e.g., "data:image/jpeg;base64,")
        const base64Data = base64.split(',')[1]
        
        const attachmentType = file.type === ACCEPTED_PDF_TYPE ? 'pdf' : 'image'
        
        resolve({
          type: attachmentType,
          media_type: file.type,
          data: base64Data,
          filename: file.name
        })
      }
      reader.onerror = () => {
        setError(`Failed to read file: ${file.name}`)
        resolve(null)
      }
      reader.readAsDataURL(file)
    })
  }, [])

  /**
   * Processes selected files and adds them to attachments.
   */
  const processFiles = useCallback(async (files: FileList | File[]) => {
    setError(null)
    
    const fileArray = Array.from(files)
    const remainingSlots = MAX_ATTACHMENTS - attachments.length
    
    if (fileArray.length > remainingSlots) {
      setError(`Can only add ${remainingSlots} more file(s). Maximum: ${MAX_ATTACHMENTS}.`)
      fileArray.splice(remainingSlots)
    }

    const newAttachments: FileAttachmentType[] = []
    
    for (const file of fileArray) {
      const attachment = await fileToAttachment(file)
      if (attachment) {
        newAttachments.push(attachment)
      }
    }

    if (newAttachments.length > 0) {
      onAttachmentsChange([...attachments, ...newAttachments])
    }
  }, [attachments, onAttachmentsChange, fileToAttachment])

  /**
   * Handles file input change event.
   */
  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      processFiles(e.target.files)
      // Reset the input so the same file can be selected again
      e.target.value = ''
    }
  }, [processFiles])

  /**
   * Handles click on the attachment area to trigger file picker.
   */
  const handleClick = useCallback(() => {
    if (!disabled && fileInputRef.current) {
      fileInputRef.current.click()
    }
  }, [disabled])

  /**
   * Handles drag enter event.
   */
  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (!disabled) {
      setIsDragging(true)
    }
  }, [disabled])

  /**
   * Handles drag leave event.
   */
  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
  }, [])

  /**
   * Handles drag over event.
   */
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
  }, [])

  /**
   * Handles file drop event.
   */
  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)

    if (disabled) return

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      processFiles(e.dataTransfer.files)
    }
  }, [disabled, processFiles])

  /**
   * Removes an attachment at the specified index.
   */
  const handleRemove = useCallback((index: number) => {
    const newAttachments = [...attachments]
    newAttachments.splice(index, 1)
    onAttachmentsChange(newAttachments)
    setError(null)
  }, [attachments, onAttachmentsChange])

  return (
    <div className="file-attachment-container">
      {/* Attachment previews */}
      {attachments.length > 0 && (
        <div className="attachment-previews">
          {attachments.map((attachment, index) => (
            <div key={index} className="attachment-preview">
              {attachment.type === 'image' ? (
                <img
                  src={`data:${attachment.media_type};base64,${attachment.data}`}
                  alt={attachment.filename}
                  className="attachment-thumbnail"
                />
              ) : (
                <div className="pdf-preview">
                  <svg
                    className="pdf-icon"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                    <polyline points="14 2 14 8 20 8" />
                    <line x1="16" y1="13" x2="8" y2="13" />
                    <line x1="16" y1="17" x2="8" y2="17" />
                    <polyline points="10 9 9 9 8 9" />
                  </svg>
                  <span className="pdf-label">PDF</span>
                </div>
              )}
              <button
                type="button"
                className="attachment-remove"
                onClick={() => handleRemove(index)}
                aria-label={`Remove ${attachment.filename}`}
              >
                ×
              </button>
              <span className="attachment-filename" title={attachment.filename}>
                {attachment.filename.length > 15
                  ? attachment.filename.substring(0, 12) + '...'
                  : attachment.filename}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Drop zone / Add button */}
      {attachments.length < MAX_ATTACHMENTS && (
        <div
          className={`attachment-dropzone ${isDragging ? 'dragging' : ''} ${disabled ? 'disabled' : ''}`}
          onClick={handleClick}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          role="button"
          tabIndex={disabled ? -1 : 0}
          aria-label="Add file attachment"
        >
          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPTED_TYPES.join(',')}
            multiple
            onChange={handleFileChange}
            style={{ display: 'none' }}
            disabled={disabled}
          />
          <svg
            className="attachment-icon"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
          </svg>
          <span className="attachment-hint">
            {isDragging ? 'Drop files here' : 'Add images or PDFs'}
          </span>
        </div>
      )}

      {/* Error message */}
      {error && (
        <div className="attachment-error">
          {error}
        </div>
      )}
    </div>
  )
}

export default FileAttachment
